/**
 * Ownership marker that stops one failed request producing two toasts.
 *
 * A failing mutation currently reaches two reporters:
 *
 *   1. the global `mutations.onError` in `shared/config/queryClient.ts`, a
 *      safety net so nothing ever fails silently — it also owns the errors
 *      only it understands (timeouts, credential-encryption failures);
 *   2. `useMutationErrorHandler`, which pages install for richer treatment —
 *      it maps VALIDATION errors onto form fields and distinguishes
 *      PERMISSION / SERVER / NETWORK.
 *
 * Both are worth keeping; what isn't worth keeping is both firing for the
 * same 500, which is what the merchant saw as two stacked toasts.
 *
 * The order is fixed by React Query: the mutation's `onError` runs BEFORE the
 * promise rejects to the caller's `catch`, so the global always goes first and
 * cannot simply check a flag the page sets later. Instead the global defers
 * one macrotask; a page handler runs in the rejection microtask, well before
 * that, and claims the error. Unclaimed errors still get the global toast, so
 * a page with no handler is unchanged apart from a sub-frame delay.
 *
 * A WeakSet keeps this off the error object itself — nothing to serialise, and
 * no retained references once the error is collected.
 */
const claimed = new WeakSet<object>();

/** Mark an error as reported, so the global fallback stays quiet. */
export function claimError(error: unknown): void {
  if (error && typeof error === "object") claimed.add(error as object);
}

export function isErrorClaimed(error: unknown): boolean {
  return !!error && typeof error === "object" && claimed.has(error as object);
}
